export const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};

/**
 * Moves an item from one list to another list.
 */
export const move = (source, destination, droppableSource, droppableDestination) => {
    const sourceClone = Array.from(source);
    const destClone = Array.from(destination);
    const [removed] = sourceClone.splice(droppableSource.index, 1);

    destClone.splice(droppableDestination.index, 0, removed);

    const result = {};
    result[droppableSource.droppableId] = sourceClone;
    result[droppableDestination.droppableId] = destClone;

    return result;
};

export function setStatusTimeTracking(chosenDate: Date) {
    const currentDate = new Date();
    const diffTime = currentDate.getTime() - chosenDate.getTime();

    const diffDays = diffTime / (1000 * 3600 * 24);
    const days = Math.round(diffDays);

    const ms = 1000 * Math.round((diffTime - days) / 1000); // round to nearest second
    const dateForHours = new Date(ms);

    return `${days} Tag(e) | ${dateForHours.getUTCHours()} st`;
}

export const salesProgressTitles = [
    { title: "Neue Immobilie", progress: "AGREE_TO_START_PRICE", next: "CONTACTED_CUSTOMER", nextTitle: "Kontakt aufgenommen" },
    { title: "Kontakt aufgenommen", progress: "CONTACTED_CUSTOMER", next: "PRICE_ASSISTANT_DONE", nextTitle: "Einwertung vorgenommen" },
    { title: "Einwertung vorgenommen", progress: "PRICE_ASSISTANT_DONE", next: "ONE_CLICK_DEAL_CLOSED", nextTitle: "Auftrag gewonnen" },
    { title: "Auftrag gewonnen", progress: "ONE_CLICK_DEAL_CLOSED", next: "AD_PREPARATION", nextTitle: "Inseratsvorbereitung" },
    { title: "Kein Interesse", progress: "LOST" },
    { title: "Inseratsvorbereitung", progress: "AD_PREPARATION", next: "INSPECTION", nextTitle: "Besichtigungsphase" },
    { title: "Besichtigungsphase", progress: "INSPECTION", next: "SALES", nextTitle: "Verkaufsphase" },
    { title: "Verkaufsphase", progress: "SALES" }
];

export type ProgressStepT = {
    title?: string;
    fullTitle?: string;
    progress?: string;
    category?:
        | "setting"
        | "closing"
        | "sale"
        | "aftersales"
        | "partnership"
        | "vr-bank-partners"
        | "marketing-content"
        | "prospective-buyer"
        | "tipper-leads"
        | "imv";
    type?: "online" | "offline" | "network";
    next?: string;
    nextTitle?: string;
    subtitle?: string;
    nextCategory?: "setting" | "closing" | "sale" | "aftersales" | "partnership" | "vr-bank-partners" | "marketing-content" | "prospective-buyer";
};

export const onlineLeadStepsTitles: ProgressStepT[] = [
    // Akquise
    // Setting (Pipeline 1)
    // Lead-Bearbeitung (Inbound) ===============================================
    // Pipeline: Lead Bearbeitung (Inbound)
    {
        title: "Neuer Lead",
        progress: "NEW_LEAD_INBOUND",
        category: "setting",
        type: "online",
        next: "INBOUND_CONTACT_ATTEMPT_1",
        nextTitle: "Kontaktversuch 1"
    },
    {
        title: "Kontaktversuch 1",
        progress: "INBOUND_CONTACT_ATTEMPT_1",
        category: "setting",
        type: "online",
        next: "INBOUND_CONTACT_ATTEMPT_2",
        nextTitle: "Kontaktversuch 2"
    },
    {
        title: "Kontaktversuch 2",
        progress: "INBOUND_CONTACT_ATTEMPT_2",
        category: "setting",
        type: "online",
        next: "INBOUND_CONTACT_ATTEMPT_3",
        nextTitle: "Kontaktversuch 3"
    },
    {
        title: "Kontaktversuch 3",
        progress: "INBOUND_CONTACT_ATTEMPT_3",
        category: "setting",
        type: "online",
        next: "POTENTIAL_PIPELINE",
        nextTitle: "In nächste Pipeline verschieben"
    },

    // Move to potential pipeline
    { title: "In nächste Pipeline verschieben", progress: "NEXT_CLOSING_PIPELINE" }
];

export const marketingContentStepsTitles: ProgressStepT[] = [
    // Akquise
    // Setting (Pipeline 1)
    // Lead-Bearbeitung (Inbound) ===============================================
    // Pipeline: Lead Bearbeitung (Inbound)
    {
        title: "Neuer Lead",
        progress: "NEW_LEAD_INBOUND_CONTENT",
        category: "marketing-content",
        next: "INBOUND_CONTACT_ATTEMPT_1_CONTENT",
        nextTitle: "Kontaktversuch 1"
    },
    {
        title: "Kontaktversuch 1",
        progress: "INBOUND_CONTACT_ATTEMPT_1_CONTENT",
        category: "marketing-content",
        next: "INBOUND_CONTACT_ATTEMPT_2_CONTENT",
        nextTitle: "Kontaktversuch 2"
    },
    {
        title: "Kontaktversuch 2",
        progress: "INBOUND_CONTACT_ATTEMPT_2_CONTENT",
        category: "marketing-content",
        next: "INBOUND_CONTACT_ATTEMPT_3_CONTENT",
        nextTitle: "Kontaktversuch 3"
    },
    {
        title: "Kontaktversuch 3",
        progress: "INBOUND_CONTACT_ATTEMPT_3_CONTENT",
        category: "marketing-content",
        next: "POTENTIAL_PIPELINE",
        nextTitle: "In nächste Pipeline verschieben"
    },

    // Move to potential pipeline
    { title: "In nächste Pipeline verschieben", progress: "NEXT_CLOSING_PIPELINE" }
];

export const offlineLeadStepsTitles: ProgressStepT[] = [
    // Akquise
    // Setting (Pipeline 1)
    // Offline Akquise ===============================================
    // Pipeline: Offline Akquise (Outbound)
    {
        title: "Neuer Lead",
        progress: "NEW_LEAD_OUTBOUND",
        category: "setting",
        type: "offline",
        next: "OUTBOUND_CONTACT_ATTEMPT_1",
        nextTitle: "Kontaktversuch 1"
    },
    {
        title: "Kontaktversuch 1",
        progress: "OUTBOUND_CONTACT_ATTEMPT_1",
        category: "setting",
        type: "offline",
        next: "OUTBOUND_CONTACT_ATTEMPT_2",
        nextTitle: "Kontaktversuch 2"
    },
    {
        title: "Kontaktversuch 2",
        progress: "OUTBOUND_CONTACT_ATTEMPT_2",
        category: "setting",
        type: "offline",
        next: "OUTBOUND_CONTACT_ATTEMPT_3",
        nextTitle: "Kontaktversuch 3"
    },
    {
        title: "Kontaktversuch 3",
        progress: "OUTBOUND_CONTACT_ATTEMPT_3",
        category: "setting",
        type: "offline",
        next: "POTENTIAL_PIPELINE",
        nextTitle: "In nächste Pipeline verschieben"
    },

    // Move to potential pipeline
    { title: "In nächste Pipeline verschieben", progress: "NEXT_CLOSING_PIPELINE" }
];

export const networkLeadStepsTitles: ProgressStepT[] = [
    // Akquise
    // Setting (Pipeline 1)
    // Netzwerk-Partner ===============================================
    // Pipeline: Netzwerk-Partner
    {
        title: "Neuer Netzwerk-Kontakt",
        progress: "NEW_NETWORK_CONTACT",
        category: "setting",
        type: "network",
        next: "NETWORK_CONTACT_ATTEMPT_1",
        nextTitle: "Kontaktversuch 1"
    },
    {
        title: "Kontaktversuch 1",
        progress: "NETWORK_CONTACT_ATTEMPT_1",
        category: "setting",
        type: "network",
        next: "NETWORK_CONTACT_ATTEMPT_2",
        nextTitle: "Kontaktversuch 2"
    },
    {
        title: "Kontaktversuch 2",
        progress: "NETWORK_CONTACT_ATTEMPT_2",
        category: "setting",
        type: "network",
        next: "NETWORK_CONTACT_ATTEMPT_3",
        nextTitle: "Kontaktversuch 3"
    },
    {
        title: "Kontaktversuch 3",
        progress: "NETWORK_CONTACT_ATTEMPT_3",
        category: "setting",
        type: "network",
        next: "NETWORK_APPOINTMENT_1",
        nextTitle: "Netzwerk-Termin 1"
    },
    {
        title: "Netzwerk-Termin 1",
        progress: "NETWORK_APPOINTMENT_1",
        category: "setting",
        type: "network",
        next: "NETWORK_APPOINTMENT_2",
        nextTitle: "Netzwerk-Termin 2"
    },
    {
        title: "Netzwerk-Termin 2",
        progress: "NETWORK_APPOINTMENT_2",
        category: "setting",
        type: "network",
        next: "NETWORK_PARTNER_GAINED",
        nextTitle: "Netzwerk-Partner gewonnen"
    },
    {
        title: "Netzwerk-Partner gewonnen",
        progress: "NETWORK_PARTNER_GAINED",
        category: "setting",
        type: "network",
        next: "NETWORK_PARTNER_ACTION_SET_UP",
        nextTitle: "Netzwerk-Partner Aktion aufgebaut"
    },
    {
        title: "Netzwerk-Partner Aktion aufgebaut",
        progress: "NETWORK_PARTNER_ACTION_SET_UP",
        category: "setting",
        type: "network",
        next: "NETWORK_PARTNER_CAMPAIGN_ROLLED_OUT",
        nextTitle: "Netzwerk-Partner Aktion ausgerollt"
    },
    {
        title: "Netzwerk-Partner Aktion ausgerollt",
        progress: "NETWORK_PARTNER_CAMPAIGN_ROLLED_OUT",
        category: "setting",
        type: "network"
    }
];

export const progressClosingStepsTitles1: ProgressStepT[] = [
    // Closing (Pipeline 2) ===============================================
    // Pipeline: Potenzial Pipeline (part 1)
    { title: "Fake-Leads", fullTitle: "Fake-Leads", progress: "FAKE_LEADS", category: "closing" },
    { title: "Kein Kontakt", fullTitle: "Kein Kontakt (Nicht erreicht / falsche Tel)", progress: "NO_CONTACT", category: "closing" },
    {
        title: "Kein Verkaufsinteresse",
        fullTitle: "Kein Verkaufsinteresse (Immobilienbesitzer)",
        progress: "NOT_INTERESTED_IN_SELLING",
        category: "closing"
    },
    {
        title: "Zu Cerberus anmelden",
        fullTitle: "Zu Cerberus anmelden",
        progress: "ADD_TO_CERBERUS",
        category: "closing"
    },
    { title: "Kalt", fullTitle: "Kalt (Verkauf in 24+ Monate)", subtitle: "(Verkauf in 24+ Monaten)", progress: "COLD", category: "closing" },
    { title: "Warm", fullTitle: "Warm (Verkauf in 6-24 Monaten)", subtitle: "(Verkauf in 6-24 Monaten)", progress: "WARM", category: "closing" },
    { title: "Heiß", fullTitle: "Heiß (Verkauf in < 6 Monaten)", subtitle: "(Verkauf in < 6 Monaten)", progress: "HOT", category: "closing" }
];

export const progressClosingStepsTitles2: ProgressStepT[] = [
    // Closing (Pipeline 2) ===============================================
    // Pipeline: Potenzial Pipeline (part 2)

    { title: "Termin vor Ort 1", progress: "APPOINTMENT_ON_SITE_1", category: "closing" },
    { title: "Termin vor Ort 2", progress: "APPOINTMENT_ON_SITE_2", category: "closing" },
    { title: "Auftrag gewonnen", progress: "ONE_CLICK_DEAL_CLOSED", category: "closing" },
    { title: "Auftrag verloren", progress: "LOST", category: "closing" },
    { title: "Importierte Immobilien", progress: "IMPORTED", category: "closing" },
    { title: "Immobilie Exportieren", progress: "EXPORTED", category: "closing" },

    // Move to sale pipeline
    { title: "In Verkaufs-Pipeline verschieben", progress: "NEXT_SALE_PIPELINE" }
];

export const progressSaleStepsTitles: ProgressStepT[] = [
    // Verkauf (Pipeline 3) ===============================================
    { title: "Unterlagenbeschaffung", progress: "DOCUMENTATION_PROCUREMENT", category: "sale", next: "EXPOSE_CREATION", nextTitle: "Exposeerstellung" },
    { title: "Exposeerstellung", progress: "EXPOSE_CREATION", category: "sale", next: "MARKETING_START", nextTitle: "Vermarktungsstart" },
    { title: "Vermarktungsstart", progress: "MARKETING_START", category: "sale", next: "VIEWING_APPOINTMENTS", nextTitle: "Besichtigungstermine" },
    { title: "Besichtigungstermine", progress: "VIEWING_APPOINTMENTS", category: "sale", next: "BUYER_CHOICE", nextTitle: "Käuferauswahl" },
    { title: "Käuferauswahl", progress: "BUYER_CHOICE", category: "sale", next: "NOTARY_APPOINTMENT", nextTitle: "Notartermin" },
    { title: "Notartermin", progress: "NOTARY_APPOINTMENT", category: "sale", next: "SOLD", nextTitle: "Verkauft" },
    { title: "Verkauf", progress: "SOLD", category: "sale" }

    // Move to after sales pipeline
    // { title: "In After Sales Pipeline verschieben", progress: "NEXT_AFTERSALES_PIPELINE" }
];

export const VRBankPartnerStepsTitles: ProgressStepT[] = [
    // VR-Bank-Partners (Pipeline 8) ===============================================
    {
        title: "Neuer Partner-Lead",
        progress: "NEW_BANK_PARTNER_LEAD",
        category: "vr-bank-partners",
        next: "BANK_PARTNER_CONTACTED",
        nextTitle: "Partner kontaktiert"
    },
    {
        title: "Partner kontaktiert",
        progress: "BANK_PARTNER_CONTACTED",
        category: "vr-bank-partners",
        next: "LEAD_CONSENT_PENDING",
        nextTitle: "Kunden Freigabe ausstehend"
    },
    {
        title: "Kunden Freigabe ausstehend",
        progress: "LEAD_CONSENT_PENDING",
        category: "vr-bank-partners",
        next: "LEAD_SENT",
        nextTitle: "Lead gesendet"
    },
    { title: "Lead gesendet", progress: "LEAD_SENT", category: "vr-bank-partners", next: "LEAD_REJECTED", nextTitle: "Lead abgelehnt" },
    // { title: "Lead angenommen", progress: "LEAD_ACCEPTED", category: "vr-bank-partners", next: "LEAD_REJECTED", nextTitle: "Lead abgelehnt" },
    {
        title: "Lead abgelehnt",
        progress: "LEAD_REJECTED",
        category: "vr-bank-partners",
        next: "BROKERAGE_CONTACT_WON",
        nextTitle: "Maklerauftrag gewonnen"
    },
    {
        title: "Maklerauftrag gewonnen",
        progress: "BROKERAGE_CONTACT_WON",
        category: "vr-bank-partners",
        next: "BROKERAGE_CONTACT_LOST",
        nextTitle: "Maklerauftrag verloren"
    },
    {
        title: "Maklerauftrag verloren",
        progress: "BROKERAGE_CONTACT_LOST",
        category: "vr-bank-partners",
        next: "REAL_ESTATE_SOLD",
        nextTitle: "Immobilie verkauft"
    },
    { title: "Immobilie verkauft", progress: "REAL_ESTATE_SOLD", category: "vr-bank-partners" }

    // Move to after sales pipeline
    // { title: "In After Sales Pipeline verschieben", progress: "NEXT_AFTERSALES_PIPELINE" }
];

export const forwardVRBankPartnerStepsTitles = ["LEAD_CONSENT_PENDING", "LEAD_SENT", "LEAD_REJECTED", "REAL_ESTATE_SOLD"];
export const backwardVRBankPartnerStepsTitles = ["NEW_BANK_PARTNER_LEAD", "BANK_PARTNER_CONTACTED"];

export const progressAftersalesStepsTitles: ProgressStepT[] = [
    // After Sales (Pipeline 4)(tbd.) ===============================================
    { title: "Empfehlungsunterlagen (Google-Bewertung / Video-Testimonial)", progress: "RECOMMENDATION_DOCUMENTS", category: "aftersales" },
    { title: "Weiterempfehlungs-Programm vorstellen", progress: "INTRODUCE_REFERRAL_PROGRAM", category: "aftersales" },
    { title: "Immobiliengeburtstag", progress: "REAL_ESTATE_BIRTHDAY", category: "aftersales" }
];

export const partnershipStepsTitles: ProgressStepT[] = [
    // Partnerschafts-Pipeline ===============================================
    { title: "Neuer Partnerschafts-Lead", progress: "NEW_PARTNERSHIP_LEAD", category: "partnership" },
    { title: "Nicht vermittelbar", progress: "NOT_TRANSFERABLE", category: "partnership" },
    { title: "Partner kontaktiert", progress: "PARTNER_CONTACTED", category: "partnership" },
    { title: "Partner hat Mandat gewonnen", progress: "PARTNER_WON_MANDATE", category: "partnership" },
    { title: "Partner hat Objekt verkauft", progress: "PARTNER_HAS_SOLD_PROPERTY", category: "partnership" },
    { title: "Kick-Back ist bezahlt", progress: "KICKBACK_IS_PAID", category: "partnership" }
];

export const partnershipUpdateStepsTitles: ProgressStepT[] = [
    // Tippgeber-Lead-Pipeline ===============================================
    { title: "Neuer Tippgeber-Lead", progress: "NEW_TIPPGEBER_LEAD", category: "tipper-leads" },
    { title: "Nicht ermittelbar", progress: "NOT_TRANSFERABLE", category: "tipper-leads" },
    { title: "Tipp kontaktiert", progress: "LEAD_CONTACTED", category: "tipper-leads" },
    { title: "In die nächste Pipeline verschieben", progress: "NEXT_CLOSING_PIPELINE", category: "tipper-leads" },
    { title: "Tippgeber informiert", progress: "TIPPGEBER_INFORMED", category: "tipper-leads" },
    { title: "Tippgeber wurde bezahlt", progress: "KICKBACK_IS_PAID", category: "tipper-leads" }
];

export const prospectiveBuyerTitles: ProgressStepT[] = [
    // Partnerschafts-Pipeline ===============================================
    {
        title: "Neue Leads",
        progress: "NEW_LEAD",
        category: "prospective-buyer",
        next: "CHECK_RE_DATABASE",
        nextTitle: "Immobilien-Datenbank Prüfen"
    },
    {
        title: "Immobilien-Datenbank Prüfen",
        progress: "CHECK_RE_DATABASE",
        category: "prospective-buyer",
        next: "CHECK_NETWORK",
        nextTitle: "Netzwerk Prüfen"
    },
    {
        title: "Netzwerk Prüfen",
        progress: "CHECK_NETWORK",
        category: "prospective-buyer",
        next: "CONTACT_ATTEMPT_1",
        nextTitle: "Kontaktversuch 1"
    },
    {
        title: "Kontaktversuch 1",
        progress: "CONTACT_ATTEMPT_1",
        category: "prospective-buyer",
        next: "CONTACT_ATTEMPT_2",
        nextTitle: "Kontaktversuch 2"
    },
    {
        title: "Kontaktversuch 2",
        progress: "CONTACT_ATTEMPT_2",
        category: "prospective-buyer",
        next: "CONTACT_ATTEMPT_3",
        nextTitle: "Kontaktversuch 3"
    },
    {
        title: "Kontaktversuch 3",
        progress: "CONTACT_ATTEMPT_3",
        category: "prospective-buyer",
        next: "RE_FOUND",
        nextTitle: "Passende Immobilie gefunden"
    },
    {
        title: "Passende Immobilie gefunden",
        progress: "RE_FOUND",
        category: "prospective-buyer",
        next: "CONTACT_EXPORTED",
        nextTitle: "Kontakte Exportieren"
    },
    {
        title: "Kontakte Exportieren",
        progress: "CONTACT_EXPORTED",
        category: "prospective-buyer",
        next: "NO_INTEREST",
        nextTitle: "Keine Interesse"
    },
    {
        title: "Keine Interesse",
        progress: "NO_INTEREST",
        category: "prospective-buyer"
    }
];

export const imvPipelineTitles: ProgressStepT[] = [
    // IMV-Pipeline ===============================================
    {
        title: "Neues Inserat",
        progress: "NEW_IMV_LEAD",
        category: "imv"
    },
    {
        title: "KV 1",
        progress: "IMV_CONTACT_ATTEMPT_1",
        category: "imv"
    },
    {
        title: "KV 2",
        progress: "IMV_CONTACT_ATTEMPT_2",
        category: "imv"
    },
    {
        title: "KV 3",
        progress: "IMV_CONTACT_ATTEMPT_3",
        category: "imv"
    },
    {
        title: "KV 4",
        progress: "IMV_CONTACT_ATTEMPT_4",
        category: "imv"
    },
    { title: "Automation starten", progress: "START_AUTOMATION", category: "imv" },
    { title: "Closing", progress: "NEXT_CLOSING_PIPELINE" }
];

export const allLeadSteps = [
    ...onlineLeadStepsTitles,
    ...offlineLeadStepsTitles,
    ...progressClosingStepsTitles1,
    ...progressClosingStepsTitles2,
    ...progressSaleStepsTitles,
    ...progressAftersalesStepsTitles,
    ...partnershipStepsTitles,
    ...VRBankPartnerStepsTitles,
    ...marketingContentStepsTitles,
    ...partnershipUpdateStepsTitles,
    ...imvPipelineTitles
];

export const generateLeadSteps = (progressType, workspace) => {
    let leadSteps;
    const hasCRMIntegration = workspace?.integrations?.propstack?.hasKey || workspace?.integrations?.onOffice?.hasCredentials;
    if (progressType === "online") leadSteps = onlineLeadStepsTitles;
    else if (progressType === "offline") leadSteps = offlineLeadStepsTitles;
    else if (progressType === "closing") {
        if (!hasCRMIntegration && !workspace?.integrations?.cerberusAccess)
            leadSteps = [
                ...progressClosingStepsTitles1.filter(item => item?.progress !== "ADD_TO_CERBERUS"),
                ...progressClosingStepsTitles2.filter(item => item?.progress !== "EXPORTED")
            ];
        else if (hasCRMIntegration && !workspace?.integrations?.cerberusAccess)
            leadSteps = [...progressClosingStepsTitles1.filter(item => item?.progress !== "ADD_TO_CERBERUS"), ...progressClosingStepsTitles2];
        else if (!hasCRMIntegration && workspace?.integrations?.cerberusAccess)
            leadSteps = [...progressClosingStepsTitles1, ...progressClosingStepsTitles2.filter(item => item?.progress !== "EXPORTED")];
        else leadSteps = [...progressClosingStepsTitles1, ...progressClosingStepsTitles2];
    } else if (progressType === "sale") leadSteps = progressSaleStepsTitles;
    else if (progressType === "aftersales") leadSteps = progressAftersalesStepsTitles;
    else if (progressType === "partnership") leadSteps = partnershipStepsTitles;
    else if (progressType === "vr-bank-partners") leadSteps = VRBankPartnerStepsTitles;
    else if (progressType === "marketing-content") leadSteps = marketingContentStepsTitles;
    else if (progressType === "tipper-leads") leadSteps = partnershipUpdateStepsTitles;
    else if (progressType === "imv") leadSteps = imvPipelineTitles;

    return leadSteps;
};

// time: 24 | 48 | 72 | 96 | 120 | 144 | 168 | 336 | 720 | 1440 | 2160
export const getActivityDate = (time, currentDate = new Date()) => {
    return new Date(currentDate.getTime() + time * 60 * 60 * 1000).toISOString();
};

export const activitiesByProgress = {
    INBOUND_CONTACT_ATTEMPT_1: [{ id: "INBOUND_CONTACT_ATTEMPT_1-1", title: "Anruf", expireTime: "24", type: "call", description: "Kontaktversuch 1" }],
    INBOUND_CONTACT_ATTEMPT_2: [{ id: "INBOUND_CONTACT_ATTEMPT_2-1", title: "Anruf", expireTime: "24", type: "call", description: "Kontaktversuch 2" }],
    INBOUND_CONTACT_ATTEMPT_3: [{ id: "INBOUND_CONTACT_ATTEMPT_3-1", title: "Anruf", expireTime: "24", type: "call", description: "Kontaktversuch 3" }],
    OUTBOUND_CONTACT_ATTEMPT_1: [{ id: "OUTBOUND_CONTACT_ATTEMPT_1-1", title: "Anruf", expireTime: "24", type: "call", description: "Kontaktversuch 1" }],
    OUTBOUND_CONTACT_ATTEMPT_2: [{ id: "OUTBOUND_CONTACT_ATTEMPT_2-1", title: "Anruf", expireTime: "24", type: "call", description: "Kontaktversuch 2" }],
    OUTBOUND_CONTACT_ATTEMPT_3: [{ id: "OUTBOUND_CONTACT_ATTEMPT_3-1", title: "Anruf", expireTime: "24", type: "call", description: "Kontaktversuch 3" }],
    NO_CONTACT: [{ id: "NO_CONTACT-1", title: "Anruf", expireTime: "720", type: "call", description: "Kein Kontakt" }],
    NOT_INTERESTED_IN_SELLING: [
        { id: "NOT_INTERESTED_IN_SELLING-1", title: "Anruf", expireTime: "720", type: "call", description: "Kein Verkaufsinteresse" }
    ],
    COLD: [{ id: "COLD-1", title: "Anruf", expireTime: "720", type: "call", description: "Kalt" }],
    WARM: [{ id: "WARM-1", title: "Anruf", expireTime: "720", type: "call", description: "Warm" }],

    HOT: [
        { id: "HOT-1", title: "Preisdossier und Wertreport versenden", expireTime: "24", type: "task" },
        { id: "HOT-2", title: "Angebot erstellen", expireTime: "24", type: "task" },
        { id: "HOT-3", title: "Maklervertrag vorbereiten & mit dem Eigentümer durchgehen", expireTime: "168", type: "task" }
    ],
    ONE_CLICK_DEAL_CLOSED: [
        { id: "ONE_CLICK_DEAL_CLOSED-1", title: "Dokumente einholen", expireTime: "168", type: "task" },
        { id: "ONE_CLICK_DEAL_CLOSED-2", title: "Vollmacht unterzeichnen lassen", expireTime: "168", type: "task" }
    ],
    EXPOSE_CREATION: [
        { id: "EXPOSE_CREATION-1", title: "Objektbilder / Videos erstellen", expireTime: "336", type: "task" },
        { id: "EXPOSE_CREATION-2", title: "Grundrisse aufbereiten", expireTime: "336", type: "task" },
        { id: "EXPOSE_CREATION-3", title: "Exposétexte verfassen", expireTime: "336", type: "task" },
        { id: "EXPOSE_CREATION-4", title: "Exposé erstellen", expireTime: "336", type: "task" },
        { id: "EXPOSE_CREATION-5", title: "Inserat erstellen", expireTime: "336", type: "task" },
        { id: "EXPOSE_CREATION-6", title: "Inserat veröffentlichen", expireTime: "336", type: "task" }
    ],
    VIEWING_APPOINTMENTS: [
        { id: "VIEWING_APPOINTMENTS-1", title: "Termine koordinieren und abgleichen", expireTime: "336", type: "task" },
        { id: "VIEWING_APPOINTMENTS-2", title: "Besichtigungen durchführen", expireTime: "336", type: "task" }
    ],
    BUYER_CHOICE: [
        { id: "BUYER_CHOICE-1", title: "Kaufvertrag vorbereiten", expireTime: "168", type: "task" },
        { id: "BUYER_CHOICE-2", title: "Bonität des Käufers prüfen", expireTime: "168", type: "task" },
        { id: "BUYER_CHOICE-3", title: "Finanzierung vermitteln", expireTime: "168", type: "task" },
        { id: "BUYER_CHOICE-4", title: "Preisverhandlungen durchführen", expireTime: "168", type: "task" }
    ],
    NOTARY_APPOINTMENT: [
        { id: "NOTARY_APPOINTMENT-1", title: "Notartermin vereinbaren", expireTime: "168", type: "task" },
        { id: "NOTARY_APPOINTMENT-2", title: "Beurkundung", expireTime: "168", type: "task" }
    ],
    SOLD: [
        { id: "SOLD-1", title: "Schlüsselübergabe", expireTime: "336", type: "task" },
        { id: "SOLD-2", title: "Rechnung erstellen und verschicken", expireTime: "336", type: "task" },
        { id: "SOLD-3", title: "Rechnungsbetrag beglichen?", expireTime: "336", type: "task" }
    ],
    IMV_CONTACT_ATTEMPT_1: [
        { id: "IMV_CONTACT_ATTEMPT_11", title: "Anruf", expireTime: "24", type: "call" },
        {
            id: "IMV_CONTACT_ATTEMPT_12",
            title: "Über ImmoScout24 kontaktieren - #1",
            expireTime: "24",
            type: "task",
            description:
                "Kontaktiere den Eigentümer mit folgender Nachricht über die angegebene Exposé-URL: Guten Tag, ich hoffe, es geht Ihnen gut. Mein Name ist [Ihr Name], und ich arbeite für Muster Makler Immobilien. Ich bin auf Ihr Inserat auf ImmoScout24 gestoßen und wollte Ihnen mein Interesse und meine Unterstützung anbieten. Mit unserer langjährigen Erfahrung im Immobilienverkauf, speziell in Köln, könnten wir Sie gezielt dabei unterstützen, Ihre Immobilie erfolgreich zu vermarkten und den besten Preis zu erzielen. Viele unserer Kunden schätzen die Entlastung durch einen professionellen Partner, der sich um alle Details kümmert – von der Vermarktung bis zum Abschluss. Ich würde mich freuen, wenn wir die Möglichkeit hätten, in einem persönlichen Gespräch telefonisch weitere Details zu besprechen. Wann passt es Ihnen am besten, damit wir uns kurz abstimmen können? Ich freue mich auf Ihre Rückmeldung. Mit freundlichen Grüßen [Dein Name] Muster Makler Immobilien",
            tags: ["ImmoScout24"]
        },
        {
            id: "IMV_CONTACT_ATTEMPT_13",
            title: "IMV - Anrufversuch 1 - mit Leitfaden",
            expireTime: "24",
            type: "call",
            description: `Telefonleitfaden für die Kaltakquise -- Einleitung ---------- Guten Tag, hier spricht [Ihr Name] von Muster Makler Immobilien. Habe ich die Ehre, mit Herrn/Frau [Name des Eigentümers] zu sprechen? Wenn bestätigt: Super! Ich hoffe, ich störe Sie gerade nicht. Ich bin auf Ihr Immobilieninserat [z. B. "für die Wohnung/das Haus in [Ort]"] aufmerksam geworden und wollte Ihnen kurz eine Frage stellen. -- Hauptteil ---------- Ist Ihre Immobilie noch verfügbar oder haben Sie bereits erste Interessenten gefunden? - Wenn Ja (noch verfügbar): Das klingt spannend. Wir von Muster Makler Immobilien haben bereits vielen Eigentümern geholfen, ihre Immobilien erfolgreich und zu besten Konditionen zu verkaufen. Uns ist dabei besonders wichtig, dass der Verkaufsprozess für Sie stressfrei und effizient abläuft. Ich würde gerne kurz mit Ihnen besprechen, wie wir Sie dabei unterstützen könnten. Dürfte ich Ihnen in den nächsten Tagen ein kurzes Angebot zukommen lassen oder vielleicht direkt einen Termin vereinbaren, um Ihre Ziele und Vorstellungen zu besprechen? - Wenn Nein (nicht mehr verfügbar): Das freut mich, dass Sie Erfolg hatten. Darf ich fragen, ob Sie den Verkauf in Eigenregie durchgeführt haben oder professionelle Unterstützung in Anspruch genommen haben? (Je nach Antwort: Anknüpfung für zukünftige Zusammenarbeit oder Empfehlung für andere Dienstleistungen) -- Abschluss ---------- Falls Sie interessiert sind, können wir sehr gerne ein unverbindliches Gespräch führen. Wann wäre es Ihnen recht, dass wir gemeinsam besprechen, wie wir Sie bestmöglich unterstützen können? Ich würde Ihnen dann alle relevanten Informationen direkt zukommen lassen. - Wenn der Eigentümer zustimmt: Wunderbar, wie wäre es mit einem Termin am [Datum] um [Uhrzeit]? Ich freue mich auf unser Gespräch. - Wenn der Eigentümer nicht sicher ist: Kein Problem, ich verstehe, dass das vielleicht nicht der richtige Zeitpunkt ist. Sollten Sie später Interesse haben oder weitere Fragen auftauchen, stehe ich Ihnen gerne zur Verfügung. -- Verabschiedung ---------- Vielen Dank für Ihre Zeit und das angenehme Gespräch. Ich freue mich auf unsere weitere Kommunikation. Einen schönen Tag noch und bleiben Sie gesund! -- Hinweise ---------- - Höflich bleiben, auch wenn der Eigentümer kein Interesse zeigt. Immer offen für zukünftige Kommunikation sein. - Auf den Gesprächspartner eingehen und gegebenenfalls das Skript flexibel anpassen.`,
            tags: ["IMV"]
        },
        {
            id: "IMV_CONTACT_ATTEMPT_14",
            title: "Über Immowelt kontaktieren - #1",
            expireTime: "24",
            type: "task",
            description: ` Kontaktiere den Eigentümer mit folgender Nachricht über die angegebene Exposé-URL: Guten Tag, ich hoffe, es geht Ihnen gut. Mein Name ist [Ihr Name], und ich arbeite für Muster Makler Immobilien. Ich bin auf Ihr Inserat auf Immowelt gestoßen und wollte Ihnen mein Interesse und meine Unterstützung anbieten. Mit unserer langjährigen Erfahrung im Immobilienverkauf, speziell in Köln, könnten wir Sie gezielt dabei unterstützen, Ihre Immobilie erfolgreich zu vermarkten und den besten Preis zu erzielen. Viele unserer Kunden schätzen die Entlastung durch einen professionellen Partner, der sich um alle Details kümmert – von der Vermarktung bis zum Abschluss. Ich würde mich freuen, wenn wir die Möglichkeit hätten, in einem persönlichen Gespräch telefonisch weitere Details zu besprechen. Wann passt es Ihnen am besten, damit wir uns kurz abstimmen können? Ich freue mich auf Ihre Rückmeldung. Mit freundlichen Grüßen [Dein Name] Muster Makler Immobilien`,
            tags: ["Immowelt"]
        },
        {
            id: "IMV_CONTACT_ATTEMPT_15",
            title: "Über Kleinanzeigen kontaktieren - #1",
            expireTime: "24",
            type: "task",
            description: `Kontaktiere den Eigentümer mit folgender Nachricht über die angegebene Exposé-URL: Guten Tag, ich hoffe, es geht Ihnen gut. Mein Name ist [Ihr Name], und ich arbeite für Muster Makler Immobilien. Ich bin auf Ihr Inserat auf Kleinanzeigen gestoßen und wollte Ihnen mein Interesse und meine Unterstützung anbieten. Mit unserer langjährigen Erfahrung im Immobilienverkauf, speziell in Köln, könnten wir Sie gezielt dabei unterstützen, Ihre Immobilie erfolgreich zu vermarkten und den besten Preis zu erzielen. Viele unserer Kunden schätzen die Entlastung durch einen professionellen Partner, der sich um alle Details kümmert – von der Vermarktung bis zum Abschluss. Ich würde mich freuen, wenn wir die Möglichkeit hätten, in einem persönlichen Gespräch telefonisch weitere Details zu besprechen. Wann passt es Ihnen am besten, damit wir uns kurz abstimmen können? Ich freue mich auf Ihre Rückmeldung. Mit freundlichen Grüßen [Dein Name] Muster Makler Immobilien`,
            tags: ["Kleinanzeigen"]
        }
    ],
    IMV_CONTACT_ATTEMPT_2: [
        {
            id: "IMV_CONTACT_ATTEMPT_21",
            title: "Über ImmoScout24 kontaktieren - #2",
            expireTime: "24",
            type: "task",
            description:
                " Guten Tag, ich hoffe, Sie hatten bisher eine angenehme Woche. Ich hatte bereits versucht, Sie bezüglich Ihres Immobilieninserats [für die Immobilie in [Ort]] zu kontaktieren, konnte Sie jedoch leider noch nicht erreichen. Gerne würde ich Ihnen dabei helfen, den Verkaufsprozess Ihrer Immobilie so erfolgreich und effizient wie möglich zu gestalten. Mit unserer Expertise bei Muster Makler Immobilien unterstützen wir Eigentümer dabei, den bestmöglichen Verkaufspreis zu erzielen und den Prozess stressfrei zu gestalten. Wenn Sie Interesse haben, würde ich mich freuen, einen Termin für ein unverbindliches Gespräch zu vereinbaren. Alternativ können Sie mich auch gerne telefonisch erreichen, um Ihre Fragen direkt zu besprechen. Ich freue mich auf Ihre Rückmeldung. Mit freundlichen Grüßen [Ihr Name] Muster Makler Immobilien",
            tags: ["ImmoScout24"]
        },
        {
            id: "IMV_CONTACT_ATTEMPT_22",
            title: "Über Immowelt kontaktieren - #2",
            expireTime: "24",
            type: "task",
            description: ` Guten Tag, ich hoffe, Sie hatten bisher eine angenehme Woche. Ich hatte bereits versucht, Sie bezüglich Ihres Immobilieninserats [für die Immobilie in [Ort]] zu kontaktieren, konnte Sie jedoch leider noch nicht erreichen. Gerne würde ich Ihnen dabei helfen, den Verkaufsprozess Ihrer Immobilie so erfolgreich und effizient wie möglich zu gestalten. Mit unserer Expertise bei Muster Makler Immobilien unterstützen wir Eigentümer dabei, den bestmöglichen Verkaufspreis zu erzielen und den Prozess stressfrei zu gestalten. Wenn Sie Interesse haben, würde ich mich freuen, einen Termin für ein unverbindliches Gespräch zu vereinbaren. Alternativ können Sie mich auch gerne telefonisch erreichen, um Ihre Fragen direkt zu besprechen. Ich freue mich auf Ihre Rückmeldung. Mit freundlichen Grüßen [Ihr Name] Muster Makler Immobilien`,
            tags: ["Immowelt"]
        },
        {
            id: "IMV_CONTACT_ATTEMPT_23",
            title: "Über Kleinanzeigen kontaktieren - #2",
            expireTime: "24",
            type: "task",
            description: `Guten Tag, ich hoffe, Sie hatten bisher eine angenehme Woche. Ich hatte bereits versucht, Sie bezüglich Ihres Immobilieninserats [für die Immobilie in [Ort]] zu kontaktieren, konnte Sie jedoch leider noch nicht erreichen. Gerne würde ich Ihnen dabei helfen, den Verkaufsprozess Ihrer Immobilie so erfolgreich und effizient wie möglich zu gestalten. Mit unserer Expertise bei Muster Makler Immobilien unterstützen wir Eigentümer dabei, den bestmöglichen Verkaufspreis zu erzielen und den Prozess stressfrei zu gestalten. Wenn Sie Interesse haben, würde ich mich freuen, einen Termin für ein unverbindliches Gespräch zu vereinbaren. Alternativ können Sie mich auch gerne telefonisch erreichen, um Ihre Fragen direkt zu besprechen. Ich freue mich auf Ihre Rückmeldung. Mit freundlichen Grüßen [Ihr Name] Muster Makler Immobilien`,
            tags: ["Kleinanzeigen"]
        },
        {
            id: "IMV_CONTACT_ATTEMPT_24",
            title: "IMV - Anrufversuch 2 - mit Leitfaden",
            expireTime: "24",
            type: "call",
            description: `Telefonleitfaden für die Kaltakquise -- Einleitung ---------- Guten Tag, hier spricht [Ihr Name] von Muster Makler Immobilien. Habe ich die Ehre, mit Herrn/Frau [Name des Eigentümers] zu sprechen? Wenn bestätigt: Super! Ich hoffe, ich störe Sie gerade nicht. Ich bin auf Ihr Immobilieninserat [z. B. "für die Wohnung/das Haus in [Ort]"] aufmerksam geworden und wollte Ihnen kurz eine Frage stellen. -- Hauptteil ---------- Ist Ihre Immobilie noch verfügbar oder haben Sie bereits erste Interessenten gefunden? - Wenn Ja (noch verfügbar): Das klingt spannend. Wir von Muster Makler Immobilien haben bereits vielen Eigentümern geholfen, ihre Immobilien erfolgreich und zu besten Konditionen zu verkaufen. Uns ist dabei besonders wichtig, dass der Verkaufsprozess für Sie stressfrei und effizient abläuft. Ich würde gerne kurz mit Ihnen besprechen, wie wir Sie dabei unterstützen könnten. Dürfte ich Ihnen in den nächsten Tagen ein kurzes Angebot zukommen lassen oder vielleicht direkt einen Termin vereinbaren, um Ihre Ziele und Vorstellungen zu besprechen? - Wenn Nein (nicht mehr verfügbar): Das freut mich, dass Sie Erfolg hatten. Darf ich fragen, ob Sie den Verkauf in Eigenregie durchgeführt haben oder professionelle Unterstützung in Anspruch genommen haben? (Je nach Antwort: Anknüpfung für zukünftige Zusammenarbeit oder Empfehlung für andere Dienstleistungen) -- Abschluss ---------- Falls Sie interessiert sind, können wir sehr gerne ein unverbindliches Gespräch führen. Wann wäre es Ihnen recht, dass wir gemeinsam besprechen, wie wir Sie bestmöglich unterstützen können? Ich würde Ihnen dann alle relevanten Informationen direkt zukommen lassen. - Wenn der Eigentümer zustimmt: Wunderbar, wie wäre es mit einem Termin am [Datum] um [Uhrzeit]? Ich freue mich auf unser Gespräch. - Wenn der Eigentümer nicht sicher ist: Kein Problem, ich verstehe, dass das vielleicht nicht der richtige Zeitpunkt ist. Sollten Sie später Interesse haben oder weitere Fragen auftauchen, stehe ich Ihnen gerne zur Verfügung. -- Verabschiedung ---------- Vielen Dank für Ihre Zeit und das angenehme Gespräch. Ich freue mich auf unsere weitere Kommunikation. Einen schönen Tag noch und bleiben Sie gesund! -- Hinweise ---------- - Höflich bleiben, auch wenn der Eigentümer kein Interesse zeigt. Immer offen für zukünftige Kommunikation sein. - Auf den Gesprächspartner eingehen und gegebenenfalls das Skript flexibel anpassen.`,
            tags: ["IMV"]
        }
    ],
    IMV_CONTACT_ATTEMPT_3: [
        {
            id: "IMV_CONTACT_ATTEMPT_31",
            title: "Über WhatsApp Kontakt aufnehmen",
            expireTime: "24",
            type: "task",
            description: "Vorlage für die WhatsApp",
            tags: ["IMV"]
        }
    ]
};

// not currently used, but needed if automatic status change and activity creation returns  ---->
// statuses for automation change of the pipeline status when all tasks of previous are done
export const automatedStatusChange = ["EXPOSE_CREATION", "VIEWING_APPOINTMENTS", "BUYER_CHOICE", "NOTARY_APPOINTMENT"];

export const networkPartnerActivities = {
    NETWORK_CONTACT_ATTEMPT_1: [{ id: "NETWORK_CONTACT_ATTEMPT_1-1", title: "Anruf", expireTime: "24", type: "call", description: "Kontaktversuch 1" }],
    NETWORK_CONTACT_ATTEMPT_2: [{ id: "NETWORK_CONTACT_ATTEMPT_2-1", title: "Anruf", expireTime: "24", type: "call", description: "Kontaktversuch 2" }],
    NETWORK_CONTACT_ATTEMPT_3: [{ id: "NETWORK_CONTACT_ATTEMPT_3-1", title: "Anruf", expireTime: "24", type: "call", description: "Kontaktversuch 3" }]
};

export const prospectiveBuyerActivities = {
    CHECK_RE_DATABASE: [{ id: "CHECK_RE_DATABASE-1", title: "Immobilien-Datenbank Prüfen", expireTime: "168", type: "task" }],
    CHECK_NETWORK: [{ id: "CHECK_NETWORK-1", title: "Netzwerk Prüfen", expireTime: "168", type: "task" }],
    CONTACT_ATTEMPT_1: [{ id: "CONTACT_ATTEMPT_1-1", title: "Anruf", expireTime: "24", type: "call", description: "Kontaktversuch 1" }],
    CONTACT_ATTEMPT_2: [{ id: "CONTACT_ATTEMPT_2-1", title: "Anruf", expireTime: "24", type: "call", description: "Kontaktversuch 2" }],
    CONTACT_ATTEMPT_3: [{ id: "CONTACT_ATTEMPT_3-1", title: "Anruf", expireTime: "24", type: "call", description: "Kontaktversuch 3" }]
};

export const networkSteps = networkLeadStepsTitles.map(st => st.progress);
export const hasDefaultActivitiesSteps = Object.keys({ ...activitiesByProgress, ...networkPartnerActivities, ...prospectiveBuyerActivities });
